import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Layout, Space, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import Title from '../../components/Title';
import CustomTable from '../../components/CustomTable/CustomTable';
import CustomModal from '../../components/CustomModal';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import { MainLogo } from '../../components/MainLogo';
import { toast } from 'react-toastify';
import {
    getPrivateIdUsers,
    createPrivateIdUser,
    updatePrivateIdUser,
    deletePrivateIdUser,
    privateIdUsers,
    privateIdUsersLoadingStatus,
} from '../../redux/slices/privateIdUsersSlice';

const initialFormData = {
    first_name: '',
    last_name: '',
    ssn: '',
    email: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    country_code: 'SE',
};

const countryOptions = [
    { id: 'SE', value: 'SE', label: 'Sweden' },
    { id: 'DK', value: 'DK', label: 'Denmark' },
];

export default function PrivateIdTestingPage() {
    const dispatch = useDispatch();

    const users = useSelector(privateIdUsers);
    const loadingStatus = useSelector(privateIdUsersLoadingStatus);

    const [userId, setUserId] = useState(null);
    const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
    const [isEditUserModalVisible, setIsEditUserModalVisible] =
        useState(false);
    const [isDeleteUserModalVisible, setIsDeleteUserModalVisible] =
        useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [selectedUser, setSelectedUser] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getPrivateIdUsers());
    }, [dispatch]);

    const toggleAddUserModal = () => {
        setFormData(initialFormData);
        setErrors({});
        setIsAddUserModalVisible(!isAddUserModalVisible);
    };

    const toggleEditUserModal = (record) => {
        if (!record) {
            setIsEditUserModalVisible(!isEditUserModalVisible);
            setFormData(initialFormData);
        } else {
            setFormData({
                ...formData,
                first_name: record.first_name,
                last_name: record.last_name,
                ssn: record.ssn,
                email: record.email,
                phone: record.phone,
                street: record.street,
                zip: record.zip,
                city: record.city,
                country_code: record.country_code,
            });
            setUserId(record.id);
            setErrors({});
            setIsEditUserModalVisible(!isEditUserModalVisible);
        }
    };

    const toggleDeleteUserModal = (record) => {
        if (!record) {
            setIsDeleteUserModalVisible(!isDeleteUserModalVisible);
            return;
        }

        setSelectedUser(record);
        setIsDeleteUserModalVisible(true);
    };

    const handleInputChange = (field) => (e) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const onSelectChange = (option, selectName) => {
        setFormData((prev) => ({
            ...prev,
            [selectName]: option.id,
        }));
    };

    const onAddUser = async () => {
        try {
            await dispatch(createPrivateIdUser(formData)).unwrap();
            toast.success('User successfully added');
            toggleAddUserModal();
            dispatch(getPrivateIdUsers());
        } catch (error) {
            setErrors(error.errors || {});
        }
    };

    const onEditUser = async () => {
        const request = {
            id: userId,
            body: formData
        }
        try {
            await dispatch(updatePrivateIdUser(request)).unwrap();
            toast.success('Changes saved successfully');
            dispatch(getPrivateIdUsers());
            toggleEditUserModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await dispatch(deletePrivateIdUser(selectedUser.id)).unwrap();
            toast.success('User deleted successfully');
            setSelectedUser(null);
            toggleDeleteUserModal();
            dispatch(getPrivateIdUsers());
        } catch (error) {
            setErrors(error.errors || {});
        }
    };

    const getFilteredUsers = (data, filter) => {
        if (!Array.isArray(data)) return [];

        const normalizedFilter = (filter || '').toLowerCase();

        if (!normalizedFilter) {
            return data;
        }

        return data.filter((user) => {
            const fieldsToSearch = [
                user.first_name,
                user.last_name,
                user.email,
                user.phone,
                user.city,
            ];

            return fieldsToSearch
                .filter(Boolean)
                .some((value) =>
                    value.toLowerCase().includes(normalizedFilter),
                );
        });
    };

    const actionsColumn = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <Tooltip placement="top" title="Edit">
                    <EditOutlined
                        onClick={() => toggleEditUserModal(record)}
                    />
                </Tooltip>
                <Tooltip placement="top" title="Delete">
                    <DeleteOutlined
                        onClick={() => toggleDeleteUserModal(record)}
                    />
                </Tooltip>
            </Space>
        ),
    };

    const isLoading = loadingStatus === 'loading';

    return (
        <Layout>
            <header className="login__header" style={{ paddingBottom: 0 }}>
                <a href="/">
                    <MainLogo />
                </a>
            </header>
            <Layout.Content className="site-layout-content">
                <Helmet>
                    <title>Console Defentry - PrivateID testing</title>
                </Helmet>
                <Title>PrivateID testing</Title>
                <div className="table-wrapper">
                    <CustomTable
                        tableHeader={[
                            'ID',
                            'First name',
                            'Last name',
                            'SSN',
                            'Email',
                            'Phone',
                            'Street',
                            'Zip',
                            'City',
                            'Country',
                            'Actions',
                        ]}
                        functionalCell={{ Actions: actionsColumn }}
                        columnsDataMap={{
                            'First name': 'first_name',
                            'Last name': 'last_name',
                            Country: 'country_code',
                        }}
                        onAddButtonClick={toggleAddUserModal}
                        tablePannelTitle="PrivateID users"
                        topPannelButtonText="Add user"
                        searchInputPlaceholder="Search by name, email, phone or city"
                        data={users}
                        getFilteredData={getFilteredUsers}
                        isLoading={isLoading}
                        withFilter
                        tableScroll={{ x: 'max-content' }}
                    />
                </div>

                <CustomModal
                    title="Add PrivateID user"
                    subText="Please fill in the user details below"
                    subTextAlign="left"
                    isModalVisible={isAddUserModalVisible}
                    onAcceptButtonClick={onAddUser}
                    onCancelButtonClick={toggleAddUserModal}
                    cancelButtonText="Cancel"
                    acceptButtonText="Add"
                >
                    <div className="form-grid">
                        <CustomInput
                            placeholder="Enter first name"
                            onInputChange={handleInputChange('first_name')}
                            inputValue={formData.first_name}
                            labelText="First name"
                            width="100%"
                            errorText={errors?.first_name}
                        />
                        <CustomInput
                            placeholder="Enter last name"
                            onInputChange={handleInputChange('last_name')}
                            inputValue={formData.last_name}
                            labelText="Last name"
                            width="100%"
                            errorText={errors?.last_name}
                        />
                        <CustomInput
                            placeholder="Enter SSN"
                            onInputChange={handleInputChange('ssn')}
                            inputValue={formData.ssn}
                            labelText="SSN"
                            width="100%"
                            errorText={errors?.ssn}
                        />
                        <CustomInput
                            placeholder="Enter email"
                            onInputChange={handleInputChange('email')}
                            inputValue={formData.email}
                            labelText="Email"
                            width="100%"
                            errorText={errors?.email}
                        />
                        <CustomInput
                            placeholder="Enter phone"
                            onInputChange={handleInputChange('phone')}
                            inputValue={formData.phone}
                            labelText="Phone"
                            width="100%"
                            errorText={errors?.phone}
                        />
                        <CustomInput
                            placeholder="Enter street"
                            onInputChange={handleInputChange('street')}
                            inputValue={formData.street}
                            labelText="Street"
                            width="100%"
                            errorText={errors?.street}
                        />
                        <CustomInput
                            placeholder="Enter ZIP"
                            onInputChange={handleInputChange('zip')}
                            inputValue={formData.zip}
                            labelText="ZIP"
                            width="100%"
                            errorText={errors?.zip}
                        />
                        <CustomInput
                            placeholder="Enter city"
                            onInputChange={handleInputChange('city')}
                            inputValue={formData.city}
                            labelText="City"
                            width="100%"
                            errorText={errors?.city}
                        />
                        <div>
                            <CustomSelect
                                name="country_code"
                                onChange={(option) =>
                                    onSelectChange(option, 'country_code')
                                }
                                options={countryOptions}
                                labelText="Country"
                                placeholder="Select country"
                                value={formData.country_code}
                                errorText={errors?.country_code}
                            />
                        </div>
                    </div>
                </CustomModal>

                <CustomModal
                    title="Edit PrivateID user"
                    subText="Please fill in the user details below"
                    subTextAlign="left"
                    isModalVisible={isEditUserModalVisible}
                    onAcceptButtonClick={onEditUser}
                    onCancelButtonClick={toggleEditUserModal}
                    cancelButtonText="Cancel"
                    acceptButtonText="Save"
                >
                    <div className="form-grid">
                        <CustomInput
                            placeholder="Enter first name"
                            onInputChange={handleInputChange('first_name')}
                            inputValue={formData.first_name}
                            labelText="First name"
                            width="100%"
                            errorText={errors?.first_name}
                        />
                        <CustomInput
                            placeholder="Enter last name"
                            onInputChange={handleInputChange('last_name')}
                            inputValue={formData.last_name}
                            labelText="Last name"
                            width="100%"
                            errorText={errors?.last_name}
                        />
                        <CustomInput
                            placeholder="Enter SSN"
                            onInputChange={handleInputChange('ssn')}
                            inputValue={formData.ssn}
                            labelText="SSN"
                            width="100%"
                            errorText={errors?.ssn}
                        />
                        <CustomInput
                            placeholder="Enter email"
                            onInputChange={handleInputChange('email')}
                            inputValue={formData.email}
                            labelText="Email"
                            width="100%"
                            errorText={errors?.email}
                        />
                        <CustomInput
                            placeholder="Enter phone"
                            onInputChange={handleInputChange('phone')}
                            inputValue={formData.phone}
                            labelText="Phone"
                            width="100%"
                            errorText={errors?.phone}
                        />
                        <CustomInput
                            placeholder="Enter street"
                            onInputChange={handleInputChange('street')}
                            inputValue={formData.street}
                            labelText="Street"
                            width="100%"
                            errorText={errors?.street}
                        />
                        <CustomInput
                            placeholder="Enter ZIP"
                            onInputChange={handleInputChange('zip')}
                            inputValue={formData.zip}
                            labelText="ZIP"
                            width="100%"
                            errorText={errors?.zip}
                        />
                        <CustomInput
                            placeholder="Enter city"
                            onInputChange={handleInputChange('city')}
                            inputValue={formData.city}
                            labelText="City"
                            width="100%"
                            errorText={errors?.city}
                        />
                        <div>
                            <CustomSelect
                                name="country_code"
                                onChange={(option) =>
                                    onSelectChange(option, 'country_code')
                                }
                                options={countryOptions}
                                labelText="Country"
                                placeholder="Select country"
                                value={formData.country_code}
                                errorText={errors?.country_code}
                            />
                        </div>
                    </div>
                </CustomModal>

                <CustomModal
                    title={
                        <p>
                            Are you sure you want to delete
                            <span className="highlighted-text">
                                {` ${selectedUser?.email || ''} `}
                            </span>
                            user?
                        </p>
                    }
                    subText="You will delete this user without recovery"
                    subTextAlign="center"
                    isModalVisible={isDeleteUserModalVisible}
                    onAcceptButtonClick={onDeleteUser}
                    onCancelButtonClick={() => toggleDeleteUserModal()}
                    cancelButtonText="Cancel"
                    acceptButtonText="Delete"
                />
            </Layout.Content>
        </Layout>
    );
}
